import { I18n } from "@assets/strings";
import React, { JSX, useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { WorkspaceLabel } from "@ui/components/group/workspaceLabel";
import { Row, row, Spacer, stack } from "@ui/components/layout";
import { Toolbar } from "@ui/components/toolbar";
import { desktop, gap, mobile } from "@ui/styles/utils";

import { fr } from "date-fns/locale/fr";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/fr-ca";
import "react-datepicker/dist/react-datepicker.css";
import { getLivecourseInfos, useParticipants } from "@domain/livecourse/hooks";
import { useUserProfile } from "@domain/user/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { LiveMembers } from "@ui/components/profile/community/liveMembers";

import { BackgroundImage } from "@ui/components/backgroundImage";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { colors } from "@ui/styles/colors";

import copy from "copy-to-clipboard";
import { ClipLoader } from "react-spinners";
import defaultLiveCourseHeader from "@assets/images/default_live_course_header.jpg";
import cameraRougeImage from "@assets/images/live/camerarouge.png";
import scheduledLiveLogoImage from "@assets/images/live/scheduled_live_logo.png";
import visioYellowImage from "@assets/images/live/visio_yellow.svg";
import { Path } from "@core/routing/path";
import { useWorkspaces } from "@domain/group/workspace/hooks";
import { LivecourseType } from "@domain/livecourse/livecourse";
import { ImageVersions } from "@domain/media/dtos";
import { Person } from "@domain/user/user";
import { useIsMobile } from "@utils/hooks";
import { DefaultButton } from "@ui/components/buttons/defaultButton";
import { CalendarIcon, CopyIcon, TimeIcon } from "@ui/components/icons";
import { Page } from "@ui/components/page";
import { UserView } from "@ui/components/profile/userView";
import { theme } from "@ui/styles/theme";
import { createToast } from "@ui/toasts/utils";

registerLocale("fr", fr);
moment.locale("fr");
interface LiveGroupcoursesInfosProps extends React.HTMLAttributes<HTMLDivElement> {
	livecourseId: string;
	shareroomId: string;
}
export const LiveGroupcoursesInfos: React.FC<LiveGroupcoursesInfosProps> = ({
	livecourseId,
	shareroomId,
	...htmlProps
}) => {
	const { result: live, loading: loadingLive } = getLivecourseInfos(livecourseId);
	const history = useHistory();
	const { result: shareroomworkspaces, loading } = useWorkspaces(shareroomId);
	const { result: members, totalElements } = useParticipants(livecourseId);
	const { result: profile } = useUserProfile(`${live?.organizer}`);
	const isMobile = useIsMobile();
	const [workspace, setWorkspace] = useState(
		loadingLive && loading ? undefined : shareroomworkspaces.find((sr) => sr && sr.id === live?.workspaceId)
	);
	const type = live?.type;
	const starthour = moment(live?.startDate).format(" hh:mma ");
	const endhour = moment(live?.endDate).format(" hh:mma ");

	useEffect(() => {
		setWorkspace(shareroomworkspaces.find((sr) => sr && sr.id === live?.workspaceId));
	}, [shareroomworkspaces, live]);

	const renderDateBloc = (scheduled: boolean): JSX.Element => (
		<>
			<DateContent>
				<Row gap={10} align="center">
					{!scheduled ? <RedVerticalBox /> : <BlueVerticalBox />}
					<Row align="center">
						{!scheduled ? (
							<Row gap={0} align="center">
								<DateElement>
									{I18n.t("live.startedat")} {starthour}
								</DateElement>
							</Row>
						) : (
							<Row gap={10} align="center">
								<Row align="center">
									<TimeIcon color="black" />
									<DateElement style={{ paddingLeft: 5 }}>
										{starthour} à {endhour}
									</DateElement>
								</Row>
								<Row align="center">
									<CalendarIcon width={18} height={18} />
									<DateElement>{moment(live?.startDate).format("DD/MM/YY")}</DateElement>
								</Row>
							</Row>
						)}
					</Row>
				</Row>
			</DateContent>
		</>
	);

	const renderRows = (scheduled: boolean): JSX.Element => (
		<FormBloc>
			<Row>
				<StyledImage
					style={{ marginTop: isMobile ? 20 : undefined, width: isMobile ? "100%" : undefined }}
					image={live?.banner}
					defaultUrl={!live?.banner ? defaultLiveCourseHeader : undefined}
					size={ImageVersions.MEDIUM}
				/>
			</Row>
			<Row style={{ marginBottom: 15 }}>
				<img src={cameraRougeImage} width={41} height={25} />
				<PrimaryTitle> {live?.title}</PrimaryTitle>
			</Row>
			<Row>{renderDateBloc(scheduled)}</Row>
			<Row>
				<SecondaryTitle style={{ display: "contents", overflow: "auto" }}> {live?.description}</SecondaryTitle>
			</Row>
			<Row style={{ marginBottom: 15, marginTop: 15 }}>Workspace</Row>
			{workspace && (
				<Row>
					<StyledWorkspaceLabel
						selected
						style={{ marginBottom: 15 }}
						workspace={workspace ? workspace.name : ""}
						small={true}
					/>
				</Row>
			)}
			<Row style={{ marginBottom: 15 }}>{I18n.t("live.createdby")}</Row>
			<Row style={{ marginBottom: 15 }}>
				<UserView user={profile as Person} />
			</Row>
			<Row style={{ marginBottom: 15 }}>
				{I18n.t("live.participant")} ({totalElements})
			</Row>
			<Row style={{ marginBottom: 15 }}>
				<LiveMembers imageSize={"default"} sample={members} count={members.length} spacing={-8} />
			</Row>
		</FormBloc>
	);

	return loadingLive || loading || !live ? (
		<ClipLoader />
	) : (
		<MainContainer containerStyle={{ paddingTop: 0 }} {...htmlProps}>
			<ContentBis>
				<Card>
					{!isMobile && (
						<Row style={{ marginBottom: 0 }}>
							{type === LivecourseType.INSTANT && (
								<Column>
									<LeftPanInstant>
										<img src={visioYellowImage} width={145} height={145} />
										<WhiteRectangle />
										<TextDescription>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanInstant>
								</Column>
							)}
							{type === LivecourseType.SCHEDULED && (
								<Column>
									<LeftPanScheduled>
										<img src={scheduledLiveLogoImage} width={145} height={"auto"} />
										<WhiteRectangle style={{ marginTop: 75 }} />
										<TextDescription>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanScheduled>
								</Column>
							)}
							{type === LivecourseType.INSTANT && renderRows(false)}
							{type === LivecourseType.SCHEDULED && renderRows(true)}
						</Row>
					)}
					{isMobile && (
						<>
							{type === LivecourseType.INSTANT && (
								<Column>
									<LeftPanInstant>
										<img src={visioYellowImage} width={70} height={70} />
										<TextDescription>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanInstant>
								</Column>
							)}
							{type === LivecourseType.SCHEDULED && (
								<Column>
									<LeftPanScheduled>
										<img style={{ top: 56 }} src={scheduledLiveLogoImage} width={70} height={"auto"} />
										<TextDescription style={{ top: 60 }}>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanScheduled>
								</Column>
							)}

							{type === LivecourseType.INSTANT && renderRows(false)}
							{type === LivecourseType.SCHEDULED && renderRows(true)}
						</>
					)}
				</Card>
			</ContentBis>

			{type === LivecourseType.SCHEDULED ? (
				<StyledToolbar>
					<Bottomnav>
						<CopyButton
							color={theme.colors.white}
							onClick={() => {
								try {
									copy(live.liveShareLink);
									createToast(I18n.t("success"), I18n.t("live.copysuccess"), "success");
								} catch (e) {
									console.error(e);
									createToast(I18n.t("error"), I18n.t("password.copyfailed"), "error");
									throw e;
								}
							}}
						>
							<CopyIcon style={{ marginRight: 13 }} />
							{I18n.t("live.copylink")}
						</CopyButton>
						<Spacer />
						<CustomButton onClick={() => history.push(Path.Shareroom({ id: shareroomId }))}>
							{I18n.t("close")}
						</CustomButton>
					</Bottomnav>
				</StyledToolbar>
			) : (
				<StyledToolbar>
					<Bottomnav>
						<Spacer />
						<PrimaryButton
							onClick={() => {
								if (global.window) {
									global.window.location.href = live.participationLink;
								}
							}}
						>
							{I18n.t("live.start")}
						</PrimaryButton>
					</Bottomnav>
				</StyledToolbar>
			)}
		</MainContainer>
	);
};

const CustomButton = styled(DefaultButton)`
	background-color: ${({ theme }) => theme.colors.secondary};
	color: ${({ theme }) => theme.colors.white};
	svg {
		margin-right: 4px;
		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}

	transition: background-color 0.2s linear;
	&:hover  {
		background-color: ${({ theme }) => theme.colors.secondaryLightTouch};
	}
`;

const CopyButton = styled(DefaultButton)`
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	color: ${({ theme }) => theme.colors.black};
	svg {
		margin-right: 4px;
		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
	font-weight: 200;
	transition: background-color 0.2s linear;
	&:hover  {
		background-color: ${({ theme }) => theme.colors.secondaryLightTouch};
	}
`;

const SecondaryTitle = styled.div`
	color: ${colors.black};
	text-align: left;
	height: 85px;
	max-height: 85px;
	font-family: DM Sans;
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 20px;
	color: #13131a;
	overflow-wrap: anywhere;
`;

const DateElement = styled.div`
	position: relative;
	font-family: Sora;
	color: #4b4b4b;
	font-size: 12px;
	font-weight: 200;
`;

const DateContent = styled(RoundedBlock)`
	display: flex;
	flex: 1;
	flex-direction: row;
	align-items: center;
	${gap(20)};
	border-color: "red";
	margin-top: 20px;
	background-color: ${({ theme }) => theme.colors.simplegray};
	height: 41px;
	width: 285px;
	margin-right: 5px;
	border-radius: 7px;
`;

const RedVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: #e83636;
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const BlueVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: ${({ theme }) => theme.colors.secondary};
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const PrimaryTitle = styled.div`
	color: ${colors.black};
	font-weight: 700;
	font-size: 1.2375rem;
	margin-left: 20px;
`;

const Bloc = styled.div`
	position: relative;
	font-family: Sora;
	margin-top: 12px;
	${({ theme }) => theme.colors.primary};
	font-size: 13px;
	font-weight: 100;
`;

const StyledWorkspaceLabel = styled(WorkspaceLabel)<{ small?: boolean }>`
	flex-shrink: 0;
	// max-width: 180px;

	${({ small }) =>
		!small &&
		css`
			position: absolute;
			right: 35px;
		`}
`;

const LeftPanScheduled = styled.div`
	@media ${desktop} {
		min-height: 680px;
		width: 120%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.secondary};
		border-radius: 21px 0px 0px 21px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		box-shadow: 0 4px 46px 0 rgb(0 0 0 / 10%);
		> img {
			position: relative;
			margin-top: 98px;
			left: 30px;
		}
	}

	@media ${mobile} {
		height: 157px;
		background-color: ${({ theme }) => theme.colors.secondary};
		border-radius: 7px 7px 7px 7px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		> img {
			position: absolute;
			top: 43px;
			left: 31px;
		}
	}
`;

const LeftPanInstant = styled.div`
	@media ${desktop} {
		min-height: 680px;
		width: 120%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.primary};
		border-radius: 21px 0px 0px 21px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		background-color: #e83636;
		box-shadow: 0 4px 46px 0 rgb(0 0 0 / 10%);
		> img {
			position: relative;
			margin-top: 71px;
			left: 30px;
		}
	}

	@media ${mobile} {
		height: 157px;
		background-color: ${({ theme }) => theme.colors.primary};
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		border-radius: 7px 7px 7px 7px;
		> img {
			position: absolute;
			top: 43px;
			left: 31px;
		}
	}
`;

const WhiteRectangle = styled.div`
	@media ${desktop} {
		position: relative;
		width: 108px;
		height: 6px;
		left: 35px;
		border-radius: 15px;
		background: ${({ theme }) => theme.colors.white};
		max-width: 234px;
		margin-top: 56px;
	}
`;

const TextDescription = styled.div`
	@media ${desktop} {
		position: relative;
		flex-direction: column;
		left: 35px;
		font-size: 16px;
		color: #ffffff;
		max-width: 234px;
		font-weight: bold;
		text-align: left;
		margin-top: 63px;
		padding-right: 7px;
		font-size: 18px;
		font-family: Sora;
		font-style: normal;
		line-height: 30px;
	}

	@media ${mobile} {
		font-family: Sora;
		font-style: normal;
		font-weight: bold;
		font-size: 16px;
		line-height: 20px;
		color: #ffffff;
		position: absolute;
		top: 58px;
		left: 126px;
		max-width: 211px;
		text-align: left;
	}
`;

const Bottomnav = styled.div`
	@media ${desktop} {
		height: 68px;
		${row(12, "flex-end", "center")};
	}
	@media ${mobile} {
		${stack(10, "center", "center", "reverse")};
	}
`;

const StyledToolbar = styled(Toolbar)`
	> * {
		flex: 1;
		max-width: 630px;
	}
`;

const ContentBis = styled(RoundedBlock)`
	padding: 40px;
	@media ${desktop} {
		padding: 0;
	}
	@media ${mobile} {
		padding: 0;
	}

	background-color: inherit;
	border: none;
	width: 100%;
	${gap(40)};
`;

export const Card = styled(RoundedBlock)`
	margin-bottom: 10%;
	@media ${desktop} {
		${gap(80)};
		margin-left: 8%;
		border-radius: 21px;
	}

	@media ${mobile} {
		border-radius: 7px;
		background-color: #f3f4f6;
	}
`;

const MainContainer = styled(Page)<{ maxWidth?: number }>`
	${stack(40, "center", "center", undefined, true)};
	@media ${desktop} {
		padding-top: 30px;
	}
`;

const Column = styled(Bloc)`
	margin-top: unset;
	border-radius: 21px 0px 0px 21px;
`;

const FormBloc = styled(Bloc)`
	@media ${desktop} {
		padding: 10px;
		padding-left: 18%;
		width: 70%;
	}

	@media ${mobile} {
		padding: 20px;
		padding-top: 0px;
		margin-top: 11px;
		border-radius: 7px;
		background-color: white;
		padding-left: 10%;
	}
`;

const StyledImage = styled(BackgroundImage)`
	border: 1px #17667869 solid;
	margin-bottom: 29px;
	border-radius: 5px;
	width: 100%;
	height: 100%;
	min-height: 149px;
	min-width: 100px;
`;
